import { createSelector } from 'reselect'
import { isCheckoutItemComponentsVariant } from 'checkout/selectors/checkoutSectionsSelectors'
import { getSelectedCruiseItem } from 'checkout/selectors/cruiseSelectors'
import getSummaryBreakdownViews from 'checkout/selectors/view/getSummaryBreakdownViews'
import { isStandaloneFlights } from 'checkout/selectors/view/flights'
import { isBNBLHotelSetDatesItem } from 'lib/checkout/checkoutUtils'

export type CheckoutSummaryPresentation = 'pending' | 'control' | 'compact' | 'standalone-flight'

interface CheckoutSummaryPresentationInput {
  variantEnabled: boolean
  isChangeDates: boolean
  hasCruiseItem: boolean
  hasBnblSetDatesWarning: boolean
  priceBreakdownSettled: boolean
  hasImportantTripNotes: boolean
  /** Standalone flight carts use their own summary, separate from the mix-cart compact summary. */
  isStandaloneFlight: boolean
}

export function resolveCheckoutSummaryPresentation(input: CheckoutSummaryPresentationInput): CheckoutSummaryPresentation {
  const {
    variantEnabled,
    isChangeDates,
    hasCruiseItem,
    hasBnblSetDatesWarning,
    priceBreakdownSettled,
    hasImportantTripNotes,
    isStandaloneFlight,
  } = input

  if (!variantEnabled || isChangeDates || hasCruiseItem || hasBnblSetDatesWarning) {
    return 'control'
  }
  if (!priceBreakdownSettled) {
    return 'pending'
  }
  if (hasImportantTripNotes) {
    return 'control'
  }
  if (isStandaloneFlight) {
    return 'standalone-flight'
  }
  return 'compact'
}

export function isCompactCheckoutSummaryFrame(presentation: CheckoutSummaryPresentation): boolean {
  return presentation !== 'control'
}

export const getCheckoutSummaryPresentation = createSelector(
  (state: App.State) => isCheckoutItemComponentsVariant(state),
  (state: App.State) => state.checkout.cart.mode === 'change-dates',
  (state: App.State) => getSelectedCruiseItem(state),
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getSummaryBreakdownViews(state),
  (state: App.State) => isStandaloneFlights(state),
  (
    variantEnabled,
    isChangeDates,
    cruiseItem,
    cartItems,
    priceBreakdown,
    isStandaloneFlight,
  ): CheckoutSummaryPresentation => resolveCheckoutSummaryPresentation({
    variantEnabled,
    isChangeDates,
    hasCruiseItem: !!cruiseItem,
    hasBnblSetDatesWarning: !!cartItems[0] && isBNBLHotelSetDatesItem(cartItems[0]),
    priceBreakdownSettled: priceBreakdown.hasRequiredData,
    hasImportantTripNotes: !!priceBreakdown.data[0]?.variation?.notes.length,
    isStandaloneFlight,
  }),
)
